// Libraries
import React, { useCallback } from 'react';

// import { useTheme } from '@emotion/react';
import { Flex, InputGroup, useOutsideClick } from '@chakra-ui/react';
import { components } from 'react-select';

// Icons
import { ArrowDown, Check, Cross } from 'assets/assetsV1/iconComponents';

import { getRandomIntBetween } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';

// Typography
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

// Styles
import { StyledFormControlV2, StyledReactSelect } from './SelectDashed.styles';

const reactSelectComponents = {
  MultiValueContainer: ({ selectProps, data }) => {
    const values = selectProps.value;
    if (values) {
      return values[values.length - 1].value === data.value ? data.label : `${data.label}, `;
    }
    return '';
  },
  DropdownIndicator: () => <ArrowDown boxSize="24px" />,
  IndicatorSeparator: () => null,
  Option: ({ ...props }) => {
    const { isSelected, children, data } = props ?? {};
    const { label, subLabel = '' } = data ?? {};
    if (label) {
      return (
        <components.Option {...props}>
          <Flex fontStyle="normal" alignItems="flex-start">
            <Flex flexDirection="column" width="100%">
              <TextBodyMedium textAlign="left" color={isSelected ? 'button.blue.focus' : 'text.g1'}>
                {label}
              </TextBodyMedium>
              {subLabel && <TextLabel color={isSelected ? 'button.blue.focus' : 'text.g2'}>{subLabel}</TextLabel>}
            </Flex>

            {isSelected && <Check ml="auto" color="button.blue.default" />}
          </Flex>
        </components.Option>
      );
    }

    return (
      <components.Option {...props}>
        <Flex alignItems="center">
          <TextBodyMedium color={isSelected ? 'button.blue.focus' : 'text.g1'}>{children}</TextBodyMedium>
          {isSelected && <Check ml="auto" color="button.blue.focus" />}
        </Flex>
      </components.Option>
    );
  },
  ClearIndicator: (props) => {
    const {
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div {...restInnerProps} ref={ref} style={{ display: 'flex', alignItems: 'center' }}>
        <Cross boxSize="16px" />
      </div>
    );
  },
};

const SelectDashed = ({
  placeholder = '',
  value,
  options,
  handleChange = (_newValue, _actionMeta) => undefined,
  customClassName = '',
  isMulti = false,
  isDisabled = false,
  className = `randomId${getRandomIntBetween(0, 100)}`,
  isSearchable = true,
  minWidth = '140px',
  isClearable = false,
  width = '100%',
  getOptionValue = undefined,
  getOptionLabel = undefined,
  boldText = false,
  selectRef = undefined,
  hideBorder = false,
  controlHeight = null,
  controlPadding = null,
  selectProps = {},
  fontStyle = 'normal',
  borderStyle = 'dashed',
  ...props
}) => {
  const ref = React.useRef();

  const [isMultiSelectDropdownOpen, setIsMultiSelectDropdownOpen] = React.useState(false);
  const handleClickOutside = useCallback(() => {
    setIsMultiSelectDropdownOpen(false);
  }, []);

  useOutsideClick({
    ref,
    handler: handleClickOutside,
  });

  const otherProps = { ...selectProps };
  if (isMulti) {
    otherProps.menuIsOpen = isMultiSelectDropdownOpen;
  }
  if (props?.onFocus) {
    otherProps.onFocus = props.onFocus;
  }
  if (props?.onBlur) {
    otherProps.onBlur = props.onBlur;
  }

  if (props?.menuPlacement) otherProps.menuPlacement = props.menuPlacement;
  if (props?.styles) otherProps.styles = props.styles;

  return (
    <StyledFormControlV2
      isDisabled={isDisabled}
      className={className}
      // {...formControlProps}
      onClick={(e) => {
        if (!isMulti) return e;
        // if clicked on options, return
        const select = document.querySelector(`.${className}`);
        const selectOptionsList = select.querySelector('.select__menu');
        if (selectOptionsList?.contains(e.target)) {
          return null;
        }

        setIsMultiSelectDropdownOpen((val) => !val);
        return null;
      }}
      ref={ref}
      isMobileView={isMobileView()}
      hideBorder={hideBorder}
      controlHeight={controlHeight}
      controlPadding={controlPadding}
      fontStyle={fontStyle}
      borderStyle={borderStyle}
    >
      <InputGroup style={{ caretColor: 'transparent', isolation: 'auto' }}>
        <StyledReactSelect
          options={options}
          getOptionValue={getOptionValue}
          getOptionLabel={getOptionLabel}
          // menuIsOpen={true}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={`select ${customClassName}`}
          classNamePrefix="select"
          isDisabled={isDisabled}
          isMulti={isMulti}
          isClearable={isClearable}
          hideSelectedOptions={false}
          isSearchable={isSearchable}
          minWidth={minWidth}
          width={width}
          components={{ ...reactSelectComponents, ...(props.components ?? {}) }}
          menuPlacement={props.menuPlacement ?? 'auto'}
          fontWeight={boldText ? '600' : '400'}
          ref={selectRef}
          {...otherProps}
          styles={{ menuPortal: (base) => ({ ...base, ...(props.menuPortalStyles ?? {}) }) }}
        />
      </InputGroup>
    </StyledFormControlV2>
  );
};

export { SelectDashed };
